const deploy_env = process.env.DEPLOY_ENV || 'development';
const SUPPORT_EMAIL = 'support@dscout.com';
const location_host = window.location.host;

/**
 * Checks if the current environment is local development
 * @returns {boolean} True if running on localhost
 */
function isLocalEnv() {
    return location_host.includes('localhost');
}

/**
 * Gets the appropriate protocol based on environment
 * @returns {string} 'http://' for local, 'https://' for others
 */
function getProtocol() {
    return isLocalEnv() ? 'http://' : 'https://';
}

/**
 * Gets the UI port for the current environment
 * @returns {string} ':3000' for local, empty string for others
 */
function getUIPort() {
    return isLocalEnv() ? ':3000' : '';
}

/**
 * Gets the API port for the current environment
 * @returns {string} ':8888' for local, empty string for others
 */
function getAPIPort() {
    return isLocalEnv() ? ':8888' : '';
}

/**
 * Gets the API prefix based on host type
 * @returns {string} Empty string for local/Dscout/custom hosts, 'api.' for panelfox.io
 */
function getAPIPrefix() {
    return isLocalEnv() || isDscoutHost(location_host) || (isCustomHost(location_host) && !isEu(location_host))
        ? ''
        : 'api.';
}

/**
 * Gets the API suffix path based on host type
 * @returns {string} '/panels-api' for Dscout/custom hosts, '/api' for panelfox.io
 */
function getAPISuffix() {
    return isDscoutHost(location_host) || (isCustomHost(location_host) && !isEu(location_host))
        ? '/panels-api'
        : '/api';
}

/**
 * Gets the OAuth suffix path based on host type
 * @returns {string} '/panels-oauth' for Dscout/custom hosts, '/oauth' for panelfox.io
 */
function getOAuthSuffix() {
    return isDscoutHost(location_host) || (isCustomHost(location_host) && !isEu(location_host))
        ? '/panels-oauth'
        : '/oauth';
}

/**
 * Gets the base URL based on environment and host type
 * @param {Object} [options] - Options for URL resolution
 * @param {boolean} [options.forceUs] - Force resolution to the US server
 * @returns {string} The appropriate URL
 */
function getBaseUrl(options = {}) {
    if (deploy_env === 'production') {
        if (options.forceUs) {
            return 'app.dscout.com';
        }
        if (isEu(location_host)) {
            return 'eu.panelfox.io';
        } else if (isDscoutHost(location_host) || isCustomHost(location_host)) {
            return 'app.dscout.com';
        } else {
            return 'panelfox.io';
        }
    } else if (deploy_env.startsWith('staging-')) {
        if (isCustomHost(location_host)) {
            return `${deploy_env}.app.dscout.com`;
        } else {
            return location_host;
        }
    } else {
        return 'localhost';
    }
}

/**
 * Gets the complete default UI URL
 * @returns {string} The full UI URL including protocol, domain, port, and suffix
 */
function getDefaultUiUrl() {
    return getProtocol() + getBaseUrl() + getUIPort() + getUISuffix();
}

/**
 * Gets the complete default API URL
 * @returns {string} The full API URL including protocol, prefix, domain, port, and suffix
 */
function getDefaultApiUrl() {
    return getProtocol() + getAPIPrefix() + getBaseUrl() + getAPIPort() + getAPISuffix();
}

/**
 * Gets the complete default OAuth URL
 * @returns {string} The full OAuth URL including protocol, prefix, domain, port, and suffix
 */
function getDefaultOauthUrl() {
    return getProtocol() + getAPIPrefix() + getBaseUrl() + getAPIPort() + getOAuthSuffix();
}

/**
 * Gets the homepage URL (external link)
 * @returns {string} The complete homepage URL
 */
function getHomepageUrl() {
    return getProtocol() + getBaseUrl() + getAPIPort() + getUISuffix();
}

/**
 * Gets the account-specific URL
 * @param {Object} account - The account object
 * @param {string} [account.custom_domain] - Optional custom domain for the account
 * @returns {string} The custom domain if available, otherwise the default UI URL
 */
function getAccountPrefixUrl(account) {
    if (account && account.custom_domain) {
        return account.custom_domain;
    } else {
        return getDefaultUiUrl();
    }
}

/**
 * Determines if a given host is a Panelfox host
 * @param {string} host - The hostname to check
 * @returns {boolean} True if the host is a Panelfox host
 */
function isPanelfoxHost(host) {
    return String(host).endsWith('panelfox.io');
}

/**
 * Determines if a given host is a Dscout host
 * @param {string} host - The hostname to check
 * @returns {boolean} True if the host is a Dscout host
 */
function isDscoutHost(host) {
    return String(host).endsWith('app.dscout.com');
}

/**
 * Determines if a given host is a custom domain
 * @param {string} host - The hostname to check
 * @returns {boolean} True if the host is a custom domain
 */
function isCustomHost(host) {
    return !isPanelfoxHost(host) && !isDscoutHost(host) && !isLocalEnv();
}

/**
 * Gets the UI suffix path based on the current host
 * @returns {string} '/panels-ui' for Dscout hosts, empty string for panelfox.io hosts
 */
function getUISuffix() {
    if (isDscoutHost(location_host)) {
        return '/panels-ui';
    } else {
        return '';
    }
}

/**
 * Checks if a host is in the EU region
 * @param {string} host - The hostname to check
 * @returns {boolean} True if the host is in the EU region
 */
function isEu(host) {
    if (
        [
            'community.angelfish-opinions.com',
            'research.bunnyfield.com',
            'community.ithoughtsresearch.com',
            'research.missiontranslate.com',
            'expert.smartconnectresearch.com',
            'eu.panelfox.io'
        ].includes(host)
    ) {
        return true;
    } else {
        return false;
    }
}

var configs = {
    production: {
        is_eu_server: isEu(location_host),
        is_dscout_host: isDscoutHost(location_host),
        getAccountPrefixUrl: getAccountPrefixUrl,
        UI_URL: getDefaultUiUrl(),
        UI_SUFFIX: getUISuffix(),
        API_URL: getDefaultApiUrl(),
        US_API_URL: 'https://' + getBaseUrl({ forceUs: true }) + '/panels-api',
        HOMEPAGE_URL: getHomepageUrl(),
        OAUTH_URL: getDefaultOauthUrl(),
        WIDGET_URL: 'https://static.panelfox.io/bundle.js',
        BENNY_TOKEN: 'bc0ed2d3-414a-4624-a2e4-ba0852893f6a',
        stripe_key_public: 'pk_live_z7a9zJ53Hkwaamh6gSYtEOaP',
        ZOOM_CLIENT_ID: '6mlJWbgqTsKxwU2qI0ATJA', // for user-managed app
        AWS_BUCKET: 'panelfox-images-prod',
        PUSHER_KEY: 'c95049b5fdeb64dc3ca4',
        PUSHER_CLUSTER: 'us3',
        MIXPANEL_TOKEN: '430792e559caba328f14491bf59c4a89',
        SUPPORT_EMAIL: SUPPORT_EMAIL,
        DSCOUT_APP_URL: 'https://app.dscout.com'
    },
    development: {
        is_eu_server: isEu(location_host),
        is_dscout_host: isDscoutHost(location_host),
        getAccountPrefixUrl: getAccountPrefixUrl,
        UI_URL: getDefaultUiUrl(),
        UI_SUFFIX: getUISuffix(),
        API_URL: getDefaultApiUrl(),
        US_API_URL: getDefaultApiUrl(),
        HOMEPAGE_URL: getHomepageUrl(),
        OAUTH_URL: getDefaultOauthUrl(),
        WIDGET_URL: 'http://localhost:8080/bundle.js',
        BENNY_TOKEN: 'f3926f36-da2c-4f95-a559-0ddd840c36cc',
        stripe_key_public: 'pk_test_9MaBepFjTJE24j6EaCIWzqLa',
        ZOOM_CLIENT_ID: '4_DYuRJTQUuygU_8qoBtMA', // for user-managed app
        AWS_BUCKET: 'panelfox-images-local',
        PUSHER_KEY: '2b0b36b5a1e86b15063e',
        PUSHER_CLUSTER: 'us3',
        MIXPANEL_TOKEN: 'f4c8754688fc1e4009b2fa3812e64189',
        SUPPORT_EMAIL: SUPPORT_EMAIL,
        DSCOUT_APP_URL: 'https://staging-4.app.dscout.com'
    },
    test: {
        is_eu_server: isEu(location_host),
        is_dscout_host: isDscoutHost(location_host),
        getAccountPrefixUrl: getAccountPrefixUrl,
        UI_URL: getDefaultUiUrl(),
        UI_SUFFIX: getUISuffix(),
        API_URL: 'http://localhost:8000/api',
        US_API_URL: 'http://localhost:8000/api',
        HOMEPAGE_URL: 'http://localhost:8000',
        OAUTH_URL: getDefaultOauthUrl(),
        WIDGET_URL: 'http://localhost:8080/bundle.js',
        BENNY_TOKEN: 'f3926f36-da2c-4f95-a559-0ddd840c36cc',
        stripe_key_public: 'pk_test_9MaBepFjTJE24j6EaCIWzqLa',
        ZOOM_CLIENT_ID: '4_DYuRJTQUuygU_8qoBtMA', // for user-managed app
        AWS_BUCKET: 'panelfox-images-local',
        PUSHER_KEY: '2b0b36b5a1e86b15063e',
        PUSHER_CLUSTER: 'us3',
        MIXPANEL_TOKEN: 'f4c8754688fc1e4009b2fa3812e64189',
        SUPPORT_EMAIL: SUPPORT_EMAIL,
        DSCOUT_APP_URL: 'https://staging-4.app.dscout.com'
    }
};

if (deploy_env.startsWith('staging-')) {
    configs[deploy_env] = {
        is_eu_server: isEu(location_host),
        is_dscout_host: isDscoutHost(location_host),
        getAccountPrefixUrl: getAccountPrefixUrl,
        UI_URL: getDefaultUiUrl(),
        UI_SUFFIX: getUISuffix(),
        API_URL: getDefaultApiUrl(),
        US_API_URL: getDefaultApiUrl(),
        HOMEPAGE_URL: getHomepageUrl(),
        OAUTH_URL: getDefaultOauthUrl(),
        WIDGET_URL: 'https://static.panelfox.io/bundle.js',
        BENNY_TOKEN: 'f3926f36-da2c-4f95-a559-0ddd840c36cc',
        stripe_key_public: 'pk_test_9MaBepFjTJE24j6EaCIWzqLa',
        ZOOM_CLIENT_ID: '4_DYuRJTQUuygU_8qoBtMA', // for user-managed app - STAGING
        AWS_BUCKET: 'panelfox-images-' + deploy_env,
        PUSHER_KEY: '2b0b36b5a1e86b15063e',
        PUSHER_CLUSTER: 'us3',
        MIXPANEL_TOKEN: 'f4c8754688fc1e4009b2fa3812e64189',
        SUPPORT_EMAIL: SUPPORT_EMAIL,
        DSCOUT_APP_URL: 'https://staging-4.app.dscout.com'
    };
}

export const isProduction = deploy_env === 'production';

export default configs[deploy_env];
